// Transportation API Services
// Handles real-time data for flights, Uber pricing, etc.

import api from '../config/api';

class TransportationAPIService {
  constructor() {
    this.uberApiKey = process.env.REACT_APP_UBER_API_KEY;
    this.lyftApiKey = process.env.REACT_APP_LYFT_API_KEY;
  }

  /**
   * Get status for any transportation item (generic method)
   * @param {Object} item - Transportation item
   * @returns {Promise<Object>} Status information
   */
  async getStatusForTransportationItem(item) {
    try {
      switch (item.type?.toLowerCase()) {
        case 'flight':
          return await this.getFlightStatus(item);
        case 'train':
          return await this.getTrainStatus(item);
        case 'bus':
          return await this.getBusStatus(item);
        case 'ferry':
          return await this.getFerryStatus(item);
        default:
          return { success: false, message: 'Status not available for this transportation type' };
      }
    } catch (error) {
      console.warn('Failed to get transportation status:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get pricing for any transportation item (generic method)
   * @param {Object} item - Transportation item
   * @returns {Promise<Object>} Pricing information
   */
  async getPricingForTransportationItem(item) {
    try {
      switch (item.type?.toLowerCase()) {
        case 'uber':
          return await this.getUberPrice(item);
        case 'lyft':
          return await this.getLyftPrice(item);
        case 'taxi':
          return await this.getTaxiPrice(item);
        case 'car_rental':
          return await this.getCarRentalPrice(item);
        default:
          return { success: false, message: 'Pricing not available for this transportation type' };
      }
    } catch (error) {
      console.warn('Failed to get transportation pricing:', error);
      return { success: false, error: error.message };
    }
  }
  async getLiveFlightStatus({ airline, flight_number, departure_date, signal } = {}) {
    try {
      const response = await api.get('/transportation/live-status', {
        params: {
          ...(airline ? { airline } : {}),
          flight_number,
          ...(departure_date ? { departure_date: String(departure_date).slice(0, 10) } : {}),
        },
        signal,
      });
      return response.data;
    } catch (error) {
      return {
        success: false,
        available: error?.response?.data?.available,
        error: error?.response?.data?.error || error.message,
      };
    }
  }

  async getFlightStatus(flightItem) {
    try {
      const live = await this.getLiveFlightStatus({
        airline: flightItem.airline,
        flight_number: flightItem.flight_number,
        departure_date: flightItem.departure_time || flightItem.departure_date,
      });

      if (live?.success) {
        return {
          success: true,
          status: live.status,
          departure_gate: live.departure?.gate,
          arrival_gate: live.arrival?.gate,
          departure_terminal: live.departure?.terminal,
          arrival_terminal: live.arrival?.terminal,
          departure_delay: live.departure?.delay,
          arrival_delay: live.arrival?.delay,
          last_updated: live.last_updated,
        };
      }

      // Fall back to the older Duffel schedule endpoint when live status is offline.
      const response = await api.get('/transportation/flight-status', {
        params: {
          airline: flightItem.airline,
          flight_number: flightItem.flight_number,
          departure_airport: flightItem.departure_airport,
          arrival_airport: flightItem.arrival_airport,
          departure_date: flightItem.departure_time
        }
      });

      return {
        success: true,
        status: response.data.status,
        departure_gate: response.data.departure_gate,
        arrival_gate: response.data.arrival_gate,
        departure_terminal: response.data.departure_terminal,
        arrival_terminal: response.data.arrival_terminal,
        departure_delay: response.data.departure_delay,
        arrival_delay: response.data.arrival_delay,
        aircraft_type: response.data.aircraft_type,
        last_updated: response.data.last_updated
      };
    } catch (error) {
      console.warn('Failed to get flight status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async lookupFlightByNumber({ flight_number, departure_date, origin, destination, signal }) {
    try {
      const response = await api.get('/transportation/flight-lookup', {
        params: {
          flight_number,
          departure_date,
          origin,
          destination,
        },
        timeout: 15000,
        signal,
      });

      return response.data;
    } catch (error) {
      console.warn('Failed to lookup flight route:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  /**
   * Get Uber pricing estimate
   * @param {Object} rideItem - Ride item with pickup_location, dropoff_location
   * @returns {Promise<Object>} Uber pricing information
   */
  async getUberPricing(rideItem) {
    try {
      console.log('🚖 Getting Uber pricing for:', rideItem.pickup_location, 'to', rideItem.dropoff_location);
      
      const response = await api.get('/uber/pricing', {
        params: {
          pickup_latitude: rideItem.pickup_latitude,
          pickup_longitude: rideItem.pickup_longitude,
          dropoff_latitude: rideItem.dropoff_latitude,
          dropoff_longitude: rideItem.dropoff_longitude,
          ride_type: rideItem.uber_type || 'uberX'
        }
      });

      return {
        success: true,
        estimates: response.data.estimates.map(estimate => ({
          product_id: estimate.product_id,
          display_name: estimate.display_name,
          estimate: estimate.estimate,
          currency_code: estimate.currency_code,
          low_estimate: estimate.low_estimate,
          high_estimate: estimate.high_estimate,
          duration: estimate.duration,
          distance: estimate.distance
        })),
        last_updated: new Date().toISOString()
      };
    } catch (error) {
      console.warn('Failed to get Uber pricing:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get Lyft pricing estimate
   * @param {Object} rideItem - Ride item with pickup_location, dropoff_location
   * @returns {Promise<Object>} Lyft pricing information
   */
  async getLyftPricing(rideItem) {
    try {
      console.log('🚗 Getting Lyft pricing for:', rideItem.pickup_location, 'to', rideItem.dropoff_location);
      
      const response = await api.get('/lyft/pricing', {
        params: {
          pickup_latitude: rideItem.pickup_latitude,
          pickup_longitude: rideItem.pickup_longitude,
          dropoff_latitude: rideItem.dropoff_latitude,
          dropoff_longitude: rideItem.dropoff_longitude,
          ride_type: rideItem.lyft_type || 'lyft'
        }
      });

      return {
        success: true,
        estimates: response.data.estimates.map(estimate => ({
          ride_type: estimate.ride_type,
          display_name: estimate.display_name,
          estimated_cost_cents_min: estimate.estimated_cost_cents_min,
          estimated_cost_cents_max: estimate.estimated_cost_cents_max,
          estimated_duration_seconds: estimate.estimated_duration_seconds,
          estimated_distance_miles: estimate.estimated_distance_miles
        })),
        last_updated: new Date().toISOString()
      };
    } catch (error) {
      console.warn('Failed to get Lyft pricing:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get car rental pricing
   * @param {Object} rentalItem - Rental item with pickup_location, dropoff_location, dates
   * @returns {Promise<Object>} Car rental pricing information
   */
  async getCarRentalPricing(rentalItem) {
    try {
      console.log('🚗 Getting car rental pricing for:', rentalItem.pickup_location);
      
      const response = await api.get('/rentalcars/pricing', {
        params: {
          pickup_location: rentalItem.pickup_location,
          dropoff_location: rentalItem.dropoff_location,
          pickup_date: rentalItem.pickup_time,
          return_date: rentalItem.return_time,
          car_type: rentalItem.car_type || 'economy'
        }
      });

      return {
        success: true,
        rentals: response.data.rentals.map(rental => ({
          company: rental.company,
          car_type: rental.car_type,
          daily_rate: rental.daily_rate,
          total_rate: rental.total_rate,
          currency: rental.currency,
          pickup_location: rental.pickup_location,
          features: rental.features
        })),
        last_updated: new Date().toISOString()
      };
    } catch (error) {
      console.warn('Failed to get car rental pricing:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get train status (Amtrak for US)
   * @param {Object} trainItem - Train item with train_number, stations
   * @returns {Promise<Object>} Train status information
   */
  async getTrainStatus(trainItem) {
    try {
      console.log('🚆 Getting train status for:', trainItem.train_number);
      
      const response = await api.get('/amtrak/train-status', {
        params: {
          train_number: trainItem.train_number,
          departure_station: trainItem.departure_station,
          arrival_station: trainItem.arrival_station,
          departure_date: trainItem.departure_time
        }
      });

      return {
        success: true,
        status: response.data.status,
        departure_delay: response.data.departure_delay,
        arrival_delay: response.data.arrival_delay,
        track: response.data.track,
        last_updated: response.data.last_updated
      };
    } catch (error) {
      console.warn('Failed to get train status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get bus status (Greyhound for US)
   * @param {Object} busItem - Bus item with bus_number, stations
   * @returns {Promise<Object>} Bus status information
   */
  async getBusStatus(busItem) {
    try {
      console.log('🚌 Getting bus status for:', busItem.bus_number);
      
      const response = await api.get('/greyhound/bus-status', {
        params: {
          bus_number: busItem.bus_number,
          departure_station: busItem.departure_station,
          arrival_station: busItem.arrival_station,
          departure_date: busItem.departure_time
        }
      });

      return {
        success: true,
        status: response.data.status,
        departure_delay: response.data.departure_delay,
        arrival_delay: response.data.arrival_delay,
        gate: response.data.gate,
        last_updated: response.data.last_updated
      };
    } catch (error) {
      console.warn('Failed to get bus status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get transit status (local transit systems)
   * @param {Object} transitItem - Transit item with line, stations
   * @returns {Promise<Object>} Transit status information
   */
  async getTransitStatus(transitItem) {
    try {
      console.log('🚇 Getting transit status for:', transitItem.line);
      
      const response = await api.get('/transit/status', {
        params: {
          line: transitItem.line,
          departure_station: transitItem.departure_station,
          arrival_station: transitItem.arrival_station,
          city: transitItem.city
        }
      });

      return {
        success: true,
        status: response.data.status,
        delays: response.data.delays,
        next_arrival: response.data.next_arrival,
        last_updated: response.data.last_updated
      };
    } catch (error) {
      console.warn('Failed to get transit status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get geocoded coordinates for a location
   * @param {string} location - Location string
   * @returns {Promise<Object>} Coordinates {lat, lng}
   */
  async getCoordinates(location) {
    try {
      const response = await api.get('/geocoding', {
        params: { city: location }
      });

      if (response.data.success) {
        return response.data.coordinates;
      }
      return null;
    } catch (error) {
      console.warn('Failed to get coordinates:', error);
      return null;
    }
  }

  /**
   * Batch get status for multiple transportation items
   * @param {Array} items - Array of transportation items
   * @returns {Promise<Object>} Status for all items
   */
  async getBatchStatus(items) {
    const results = {};
    
    for (const item of items) {
      if (item.type === 'flight') {
        results[item.id] = await this.getFlightStatus(item);
      } else if (item.type === 'train') {
        results[item.id] = await this.getTrainStatus(item);
      } else if (item.type === 'bus') {
        results[item.id] = await this.getBusStatus(item);
      } else if (item.type === 'subway') {
        results[item.id] = await this.getTransitStatus(item);
      }
    }

    return results;
  }

  /**
   * Batch get pricing for multiple transportation items
   * @param {Array} items - Array of transportation items
   * @returns {Promise<Object>} Pricing for all items
   */
  async getBatchPricing(items) {
    const results = {};
    
    for (const item of items) {
      if (item.type === 'uber' || item.type === 'taxi') {
        results[item.id] = await this.getUberPricing(item);
      } else if (item.type === 'lyft') {
        results[item.id] = await this.getLyftPricing(item);
      } else if (item.type === 'car_rental') {
        results[item.id] = await this.getCarRentalPricing(item);
      }
    }

    return results;
  }
}

const transportationAPI = new TransportationAPIService();
export default transportationAPI;
